import { useMemo, useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useReasoningStream } from "@/hooks/use-reasoning-stream";
import { ManualScenarioForm } from "./scenarios-tab/manual-scenario-form";

void useReasoningStream;

export interface GenerateScenarioModalProps {
  projectId: string;
  onClose: () => void;
  initialMode?: "agent" | "manual";
  initialPersonaId?: string;
}

export function GenerateScenarioModal({
  projectId,
  onClose,
  initialMode = "manual",
  initialPersonaId,
}: GenerateScenarioModalProps) {
  const [mode, setMode] = useState<"agent" | "manual">(initialMode);
  const [agentCapId, setAgentCapId] = useState<string>("__any__");
  const [agentPersonaId, setAgentPersonaId] = useState<string>(
    initialPersonaId ?? "__any__",
  );
  const [intent, setIntent] = useState("");
  const [copied, setCopied] = useState(false);

  const { data: capabilitiesList } = trpc.capability.list.useQuery({
    projectId,
  });
  const { data: personasList } = trpc.persona.list.useQuery({ projectId });

  const capabilities = capabilitiesList ?? [];
  const activePersonas = useMemo(
    () => (personasList ?? []).filter((p) => p.status !== "archived"),
    [personasList],
  );

  const selectedCapability = capabilities.find((c) => c.id === agentCapId);
  const selectedPersona = activePersonas.find((p) => p.id === agentPersonaId);

  const promptText = useMemo(() => {
    const trimmedIntent = intent.trim();
    const hasSpecifics =
      Boolean(selectedCapability) ||
      Boolean(selectedPersona) ||
      trimmedIntent.length > 0;

    if (!hasSpecifics) {
      return [
        "/iamu-propose",
        "Review our project capabilities, personas, and routes, then propose and author the highest-priority missing scenario with human tests.",
      ].join("\n");
    }

    const lines = ["/iamu-new"];
    if (selectedCapability) {
      lines.push(`Capability: ${selectedCapability.name}`);
    }
    if (selectedPersona) {
      lines.push(`Persona: ${selectedPersona.name} (${selectedPersona.role})`);
    }
    if (trimmedIntent) {
      lines.push(`Situation to test: ${trimmedIntent}`);
    }
    lines.push(
      "Author the scenario in domain language with a clear tension and 2-3 human tests, bind the persona, and stage the entry route.",
    );
    return lines.join("\n");
  }, [selectedCapability, selectedPersona, intent]);

  async function handleCopyPrompt() {
    try {
      await navigator.clipboard.writeText(promptText);
      setCopied(true);
      toast.success("Copied prompt for your coding agent");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy prompt to clipboard");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>New scenario</DialogTitle>
          <DialogDescription>
            Write a scenario directly in the browser or copy a prompt for your
            coding agent.
          </DialogDescription>
        </DialogHeader>

        {/* Two-mode segmented control */}
        <div
          role="tablist"
          aria-label="Scenario creation mode"
          className="grid shrink-0 grid-cols-2 gap-1 rounded-md border border-border/60 bg-muted/40 p-1"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === "manual"}
            onClick={() => setMode("manual")}
            className={`rounded px-3 py-1.5 text-sm transition-colors ${
              mode === "manual"
                ? "bg-foreground text-background font-semibold shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Write manually
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "agent"}
            onClick={() => setMode("agent")}
            className={`rounded px-3 py-1.5 text-sm transition-colors ${
              mode === "agent"
                ? "bg-foreground text-background font-semibold shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Draft with coding agent
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {mode === "agent" ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Copy a prompt for Claude Code or Cursor to author and stage a
                scenario grounded in your codebase and routes.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="min-w-0 space-y-1.5">
                  <Label htmlFor="agent-scenario-capability">Capability</Label>
                  <Select value={agentCapId} onValueChange={setAgentCapId}>
                    <SelectTrigger
                      id="agent-scenario-capability"
                      className="w-full min-w-0 max-w-full overflow-hidden"
                    >
                      <SelectValue placeholder="Any capability" />
                    </SelectTrigger>
                    <SelectContent
                      position="popper"
                      className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]"
                    >
                      <SelectItem value="__any__">Any capability</SelectItem>
                      {capabilities.map((cap) => (
                        <SelectItem key={cap.id} value={cap.id}>
                          {cap.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="min-w-0 space-y-1.5">
                  <Label htmlFor="agent-scenario-persona">Persona</Label>
                  <Select
                    value={agentPersonaId}
                    onValueChange={setAgentPersonaId}
                  >
                    <SelectTrigger
                      id="agent-scenario-persona"
                      className="w-full min-w-0 max-w-full overflow-hidden"
                    >
                      <SelectValue placeholder="Any persona" />
                    </SelectTrigger>
                    <SelectContent
                      position="popper"
                      className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]"
                    >
                      <SelectItem value="__any__">Any persona</SelectItem>
                      {activePersonas.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} — {p.role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="agent-scenario-intent">
                  What situation or flow should we test?
                </Label>
                <Textarea
                  id="agent-scenario-intent"
                  value={intent}
                  onChange={(e) => setIntent(e.target.value)}
                  placeholder="A consultant tries to submit a claim when a receipt is missing..."
                  rows={3}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Prompt preview</Label>
                <div className="rounded-md border border-border bg-muted/40 p-3 font-mono text-xs whitespace-pre-wrap">
                  {promptText}
                </div>
              </div>

              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleCopyPrompt}>
                  {copied ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2 h-4 w-4" />
                      Copy prompt
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <ManualScenarioForm
              projectId={projectId}
              capabilities={capabilities}
              personas={activePersonas}
              initialPersonaId={initialPersonaId}
              onCancel={onClose}
              onSuccess={onClose}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
